/**
 * Chemistry-check demo fixtures — one case per PRD acceptance scenario so the
 * shadow-mode walkthrough covers the real workflow: happy path, assistant as
 * scheduling contact, ambiguous time zone, no-overlap escalation, awaiting
 * selection, and a reschedule with a preserved audit trail.
 *
 * Timestamps are computed relative to "now" so the queue always looks live.
 */

import type {
  ChemistryAvailabilityParse,
  ChemistryCase,
  ChemistryKickoffParse,
} from '../types';

/** ISO timestamp `days` from today at local hour:minute. */
function at(days: number, hour: number, minute = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

/** Date-only ISO (YYYY-MM-DD) `days` from today. */
function day(days: number): string {
  return at(days, 12).slice(0, 10);
}

export const CHEMISTRY_CASES_MOCK: ChemistryCase[] = [
  // ── CC-0142 · three-coach happy path, mid-flight ─────────────────────────
  {
    id: 'cc-demo-142',
    tag: 'CC-0142',
    clientName: 'Jamie Chen',
    clientEmail: 'jchen@meridiancap.com',
    clientTimezone: 'America/New_York',
    assistantName: null,
    assistantEmail: null,
    pmName: 'Christine Lane',
    pmEmail: 'clane@arielgroup.com',
    state: 'collecting',
    targetDate: day(12),
    createdAt: at(-3, 9, 12),
    waitingOn: 'Coach (Morgan Lee)',
    nextAction: 'Send first reminder to Morgan Lee if no reply',
    nextActionAt: at(1, 9),
    escalationReason: null,
    tracks: [
      {
        id: 't-142-1',
        coachName: 'Alex Rivera',
        coachEmail: 'alex@riveracoaching.com',
        coachTimezone: 'America/Chicago',
        state: 'scheduled',
        waitingOn: 'Nobody — meeting confirmed',
        nextAction: 'Confirm completion after the meeting passes',
        nextActionAt: at(5, 17),
        scheduledStart: at(5, 10),
        scheduledEnd: at(5, 10, 45),
        availability: [
          {
            party: 'coach',
            sourceText: 'Tuesday 10–12 or Wednesday morning both work on my end',
            startIso: at(5, 10),
            endIso: at(5, 12),
            timezone: 'America/Chicago',
            status: 'consumed',
          },
        ],
      },
      {
        id: 't-142-2',
        coachName: 'Priya Shah',
        coachEmail: 'priya@shahleadership.com',
        coachTimezone: 'America/New_York',
        state: 'ready_to_schedule',
        waitingOn: 'Agent — invite pending',
        nextAction: 'Create Outlook invite for the verified overlap',
        nextActionAt: at(0, 16),
        proposedStart: at(6, 10),
        proposedEnd: at(6, 10, 45),
        scheduledStart: null,
        scheduledEnd: null,
        availability: [
          {
            party: 'coach',
            sourceText: 'Wednesday between 10 and 1 Eastern, or Thursday after 3',
            startIso: at(6, 10),
            endIso: at(6, 13),
            timezone: 'America/New_York',
            status: 'valid',
          },
        ],
      },
      {
        id: 't-142-3',
        coachName: 'Morgan Lee',
        coachEmail: 'morgan@leeadvisory.com',
        coachTimezone: null,
        state: 'waiting_coach',
        waitingOn: 'Coach (Morgan Lee)',
        nextAction: 'Send first reminder to Morgan Lee if no reply',
        nextActionAt: at(1, 9),
        scheduledStart: null,
        scheduledEnd: null,
        availability: [],
      },
    ],
    timeline: [
      { at: at(-3, 9, 12), actor: 'pm', kind: 'email_in', title: 'Kickoff received from Christine Lane', detail: 'Coordinate chemistry checks for Jamie Chen with Alex Rivera, Priya Shah, and Morgan Lee. Jamie is Eastern. Complete by target date.' },
      { at: at(-3, 9, 13), actor: 'agent', kind: 'parse', title: 'Case created with 3 meeting tracks', detail: 'Client, PM, 3 coaches, and target date all present — parsed case echoed to Christine for confirmation.' },
      { at: at(-3, 9, 45), actor: 'agent', kind: 'email_out', title: 'Availability request sent to Jamie Chen', detail: 'Asked for 3–5 windows over the next two weeks, Eastern time.' },
      { at: at(-2, 14, 5), actor: 'client', kind: 'email_in', title: 'Jamie replied with availability', detail: '“I could do Tuesday or Wednesday next week between 10 and noon, or Thursday after 2 — all Eastern.”' },
      { at: at(-2, 14, 6), actor: 'agent', kind: 'parse', title: '3 windows normalized (America/New_York)', detail: 'Tue 10:00–12:00 · Wed 10:00–12:00 · Thu 14:00–17:00 (assumed end of business, flagged low-risk).' },
      { at: at(-2, 14, 30), actor: 'agent', kind: 'email_out', title: 'Availability sent to each coach separately', detail: 'Three separate threads — no coach can see the others. Windows rendered in each coach’s zone where known.' },
      { at: at(-1, 8, 50), actor: 'coach', kind: 'email_in', title: 'Alex Rivera confirmed two options', detail: '“Tuesday 10–12 or Wednesday morning both work on my end.” (Central)' },
      { at: at(-1, 8, 52), actor: 'agent', kind: 'match', title: 'Overlap verified for Alex Rivera', detail: 'Earliest reasonable slot: Tuesday 10:00–10:45 Central / 11:00 Eastern. Calendar re-checked before booking.' },
      { at: at(-1, 9, 0), actor: 'agent', kind: 'event_created', title: 'Outlook invite created — Jamie Chen × Alex Rivera', detail: 'Teams link attached · case tag CC-0142 in description · confirmation email sent to both.' },
      { at: at(0, 11, 20), actor: 'coach', kind: 'email_in', title: 'Priya Shah replied with availability', detail: '“Wednesday between 10 and 1 Eastern, or Thursday after 3.”' },
      { at: at(0, 11, 22), actor: 'agent', kind: 'match', title: 'Overlap found for Priya Shah', detail: 'Wednesday 10:00–10:45 Eastern fits Jamie’s Wednesday window with no calendar conflicts.' },
      { at: at(0, 11, 22), actor: 'agent', kind: 'proposal', title: 'Would create Outlook invite — Jamie Chen × Priya Shah', detail: 'Shadow mode: invite prepared but not sent. Wednesday 10:00 ET, 45 minutes, Teams.', shadow: true },
      { at: at(0, 9, 0), actor: 'system', kind: 'note', title: 'Morgan Lee has not replied', detail: 'First reminder queued for tomorrow 9:00 (2 business days after outreach). Max two reminders, then escalate to Christine.' },
    ],
  },

  // ── CC-0139 · assistant-managed client, fully scheduled ──────────────────
  {
    id: 'cc-demo-139',
    tag: 'CC-0139',
    clientName: 'Rosa Delgado',
    clientEmail: 'rdelgado@vantagehealth.com',
    clientTimezone: 'America/Los_Angeles',
    assistantName: 'Taylor Kim',
    assistantEmail: 'tkim@vantagehealth.com',
    pmName: 'Danielle Neydorff',
    pmEmail: 'dneydorff@arielgroup.com',
    state: 'scheduled',
    targetDate: day(9),
    createdAt: at(-6, 10, 30),
    waitingOn: 'Nobody — all three meetings confirmed',
    nextAction: 'Confirm completion as each meeting passes',
    nextActionAt: at(3, 17),
    escalationReason: null,
    tracks: [
      {
        id: 't-139-1',
        coachName: 'Sam Okafor',
        coachEmail: 'sam@okaforcoaching.com',
        coachTimezone: 'America/New_York',
        state: 'scheduled',
        waitingOn: 'Nobody — meeting confirmed',
        nextAction: 'Confirm completion after the meeting passes',
        nextActionAt: at(3, 17),
        scheduledStart: at(3, 9),
        scheduledEnd: at(3, 9, 45),
        availability: [
          { party: 'coach', sourceText: 'Any morning next week before 1pm Eastern', startIso: at(3, 9), endIso: at(3, 13), timezone: 'America/New_York', status: 'consumed' },
        ],
      },
      {
        id: 't-139-2',
        coachName: 'Lena Fischer',
        coachEmail: 'lena@fischerexec.com',
        coachTimezone: 'Europe/Berlin',
        state: 'scheduled',
        waitingOn: 'Nobody — meeting confirmed',
        nextAction: 'Confirm completion after the meeting passes',
        nextActionAt: at(4, 20),
        scheduledStart: at(4, 8),
        scheduledEnd: at(4, 8, 45),
        availability: [
          { party: 'coach', sourceText: 'Late afternoon Berlin time works best — 17:00 or 18:00', startIso: at(4, 8), endIso: at(4, 10), timezone: 'Europe/Berlin', status: 'consumed' },
        ],
      },
      {
        id: 't-139-3',
        coachName: 'David Tran',
        coachEmail: 'david@tranleadership.com',
        coachTimezone: 'America/Los_Angeles',
        state: 'scheduled',
        waitingOn: 'Nobody — meeting confirmed',
        nextAction: 'Confirm completion after the meeting passes',
        nextActionAt: at(6, 17),
        scheduledStart: at(6, 14),
        scheduledEnd: at(6, 14, 45),
        availability: [
          { party: 'coach', sourceText: 'Afternoons Pacific, ideally 2 or 3', startIso: at(6, 14), endIso: at(6, 16), timezone: 'America/Los_Angeles', status: 'consumed' },
        ],
      },
    ],
    timeline: [
      { at: at(-6, 10, 30), actor: 'pm', kind: 'email_in', title: 'Kickoff received from Danielle Neydorff', detail: 'Rosa Delgado with three coaches; assistant Taylor Kim is the scheduling contact and should be copied.' },
      { at: at(-6, 10, 31), actor: 'agent', kind: 'parse', title: 'Case created — assistant registered as scheduling contact', detail: 'Taylor Kim receives scheduling email; Rosa Delgado remains the meeting attendee on every invite.' },
      { at: at(-6, 11, 0), actor: 'agent', kind: 'email_out', title: 'Availability request sent to Taylor Kim (assistant)', detail: 'Rosa copied per communication policy.' },
      { at: at(-5, 16, 40), actor: 'assistant', kind: 'email_in', title: 'Taylor supplied Rosa’s availability', detail: '“Rosa can do early mornings any day next week, or Thursday/Friday 2–4 Pacific.”' },
      { at: at(-5, 16, 42), actor: 'agent', kind: 'parse', title: '4 windows normalized (America/Los_Angeles)' },
      { at: at(-5, 17, 0), actor: 'agent', kind: 'email_out', title: 'Availability sent to each coach separately' },
      { at: at(-4, 9, 15), actor: 'agent', kind: 'event_created', title: 'Invite created — Rosa Delgado × Sam Okafor', detail: 'Attendees: Rosa (attendee), Taylor (optional), Sam. Teams link. Tag CC-0139.' },
      { at: at(-3, 12, 5), actor: 'agent', kind: 'event_created', title: 'Invite created — Rosa Delgado × Lena Fischer', detail: 'Cross-zone: 8:00 Pacific / 17:00 Berlin — both stated explicitly in the confirmation email.' },
      { at: at(-2, 15, 30), actor: 'agent', kind: 'event_created', title: 'Invite created — Rosa Delgado × David Tran' },
      { at: at(-2, 15, 35), actor: 'system', kind: 'note', title: 'All tracks scheduled', detail: 'Completion checks queued for the day after each meeting.' },
    ],
  },

  // ── CC-0145 · ambiguous time zone → needs human ──────────────────────────
  {
    id: 'cc-demo-145',
    tag: 'CC-0145',
    clientName: 'Wei Zhang',
    clientEmail: 'wzhang@atlasadvisors.com',
    clientTimezone: 'America/Chicago',
    assistantName: null,
    assistantEmail: null,
    pmName: 'Christine Lane',
    pmEmail: 'clane@arielgroup.com',
    state: 'needs_human',
    targetDate: day(8),
    createdAt: at(-2, 13, 20),
    waitingOn: 'PM (Christine Lane)',
    nextAction: 'Review drafted clarification before it goes to the client',
    nextActionAt: at(0, 15),
    escalationReason:
      'Wei wrote “Tuesday at 2 works” with no time zone, and he is traveling this month (kickoff noted London → Chicago). Time-zone confidence is below threshold — a clarification email is drafted for review rather than guessed.',
    tracks: [
      {
        id: 't-145-1',
        coachName: 'Maya Patel',
        coachEmail: 'maya@patelcoaching.com',
        coachTimezone: 'America/New_York',
        state: 'needs_human',
        waitingOn: 'PM (Christine Lane)',
        nextAction: 'Approve clarification email to Wei',
        nextActionAt: at(0, 15),
        scheduledStart: null,
        scheduledEnd: null,
        availability: [],
      },
      {
        id: 't-145-2',
        coachName: 'Jonas Weber',
        coachEmail: 'jonas@weberleadership.com',
        coachTimezone: 'Europe/London',
        state: 'waiting_client',
        waitingOn: 'Client (Wei Zhang)',
        nextAction: 'Resume once usable client windows exist',
        nextActionAt: at(1, 9),
        scheduledStart: null,
        scheduledEnd: null,
        availability: [],
      },
    ],
    timeline: [
      { at: at(-2, 13, 20), actor: 'pm', kind: 'email_in', title: 'Kickoff received from Christine Lane', detail: 'Wei Zhang with Maya Patel and Jonas Weber. Note: “Wei is traveling — London this week, back to Chicago after.”' },
      { at: at(-2, 13, 40), actor: 'agent', kind: 'email_out', title: 'Availability request sent to Wei Zhang', detail: 'Asked for windows with an explicit time zone given the travel.' },
      { at: at(-1, 7, 55), actor: 'client', kind: 'email_in', title: 'Wei replied', detail: '“Tuesday at 2 works. Otherwise Friday morning.”' },
      { at: at(-1, 7, 56), actor: 'agent', kind: 'parse', title: 'Parse flagged: no usable time zone', detail: '“Tuesday at 2” could be London or Chicago — a 6-hour difference. Policy: never guess a consequential detail.' },
      { at: at(-1, 8, 0), actor: 'agent', kind: 'escalation', title: 'Escalated to Christine with a drafted clarification', detail: 'Draft asks Wei to confirm the zone (“2pm Chicago or 2pm London?”) — one click to approve and send.', shadow: true },
    ],
  },

  // ── CC-0137 · no overlap after two rounds → escalated ────────────────────
  {
    id: 'cc-demo-137',
    tag: 'CC-0137',
    clientName: 'Amara Osei',
    clientEmail: 'aosei@brightlinegroup.com',
    clientTimezone: 'Europe/London',
    assistantName: null,
    assistantEmail: null,
    pmName: 'Danielle Neydorff',
    pmEmail: 'dneydorff@arielgroup.com',
    state: 'needs_human',
    targetDate: day(5),
    createdAt: at(-9, 8, 45),
    waitingOn: 'PM (Danielle Neydorff)',
    nextAction: 'Decide: extend target date, drop Nicole Barnes, or ask her for off-hours slot',
    nextActionAt: at(0, 12),
    escalationReason:
      'Two availability rounds with Nicole Barnes produced no overlap with Amara’s London windows (Nicole is Pacific and blocked mornings). Attempted options are attached — this needs a human call.',
    tracks: [
      {
        id: 't-137-1',
        coachName: 'Tom Whitfield',
        coachEmail: 'tom@whitfieldadvisory.com',
        coachTimezone: 'Europe/London',
        state: 'complete',
        waitingOn: 'Nobody — complete',
        nextAction: 'None',
        nextActionAt: at(2, 9),
        scheduledStart: at(-4, 14),
        scheduledEnd: at(-4, 14, 45),
        availability: [],
      },
      {
        id: 't-137-2',
        coachName: 'Ingrid Halvorsen',
        coachEmail: 'ingrid@halvorsencoaching.com',
        coachTimezone: 'Europe/Oslo',
        state: 'complete',
        waitingOn: 'Nobody — complete',
        nextAction: 'None',
        nextActionAt: at(2, 9),
        scheduledStart: at(-3, 11),
        scheduledEnd: at(-3, 11, 45),
        availability: [],
      },
      {
        id: 't-137-3',
        coachName: 'Nicole Barnes',
        coachEmail: 'nicole@barnesleadership.com',
        coachTimezone: 'America/Los_Angeles',
        state: 'needs_human',
        waitingOn: 'PM (Danielle Neydorff)',
        nextAction: 'PM decision on how to proceed',
        nextActionAt: at(0, 12),
        scheduledStart: null,
        scheduledEnd: null,
        availability: [
          { party: 'client', sourceText: 'Weekdays 9–12 London, plus Tuesday to 2pm', startIso: at(-7, 9), endIso: at(-7, 12), timezone: 'Europe/London', status: 'expired' },
          { party: 'coach', sourceText: 'Afternoons Pacific only — mornings are blocked through month end', startIso: at(-6, 13), endIso: at(-6, 17), timezone: 'America/Los_Angeles', status: 'expired' },
        ],
      },
    ],
    timeline: [
      { at: at(-9, 8, 45), actor: 'pm', kind: 'email_in', title: 'Kickoff received from Danielle Neydorff' },
      { at: at(-8, 10, 0), actor: 'agent', kind: 'event_created', title: 'Invite created — Amara Osei × Tom Whitfield' },
      { at: at(-7, 9, 30), actor: 'agent', kind: 'event_created', title: 'Invite created — Amara Osei × Ingrid Halvorsen' },
      { at: at(-7, 12, 0), actor: 'agent', kind: 'match', title: 'No overlap — Amara × Nicole Barnes (round 1)', detail: 'Amara: London mornings. Nicole: Pacific afternoons (= London evenings). Zero intersection.' },
      { at: at(-7, 12, 10), actor: 'agent', kind: 'email_out', title: 'Asked Nicole (more constrained party) for additional options' },
      { at: at(-5, 18, 20), actor: 'coach', kind: 'email_in', title: 'Nicole replied — still afternoons only', detail: '“Mornings are blocked through month end, sorry.”' },
      { at: at(-5, 18, 22), actor: 'agent', kind: 'match', title: 'No overlap after round 2 — stopping per policy', detail: 'Two rounds is the configured limit before a human decides.' },
      { at: at(-5, 18, 25), actor: 'agent', kind: 'escalation', title: 'Escalated to Danielle with full history', detail: 'Concise summary + every attempted window attached. Options: extend target, swap coach, or request an off-hours exception.' },
      { at: at(-4, 15, 0), actor: 'pm', kind: 'note', title: 'Tom Whitfield check confirmed complete by Danielle' },
      { at: at(-3, 12, 30), actor: 'pm', kind: 'note', title: 'Ingrid Halvorsen check confirmed complete by Danielle' },
    ],
  },

  // ── CC-0133 · all checks complete → awaiting selection ───────────────────
  {
    id: 'cc-demo-133',
    tag: 'CC-0133',
    clientName: 'Priya Raman',
    clientEmail: 'praman@corvustech.com',
    clientTimezone: 'America/Chicago',
    assistantName: null,
    assistantEmail: null,
    pmName: 'Christine Lane',
    pmEmail: 'clane@arielgroup.com',
    state: 'awaiting_selection',
    targetDate: day(-2),
    createdAt: at(-16, 9, 0),
    waitingOn: 'PM (Christine Lane)',
    nextAction: 'Prompt Christine to capture Priya’s coach selection',
    nextActionAt: at(1, 9),
    escalationReason: null,
    tracks: [
      {
        id: 't-133-1',
        coachName: 'Elena Sorokina',
        coachEmail: 'elena@sorokinacoaching.com',
        coachTimezone: 'America/New_York',
        state: 'complete',
        waitingOn: 'Nobody — complete',
        nextAction: 'None',
        nextActionAt: at(1, 9),
        scheduledStart: at(-6, 10),
        scheduledEnd: at(-6, 10, 45),
        availability: [],
      },
      {
        id: 't-133-2',
        coachName: 'Marcus Hill',
        coachEmail: 'marcus@hillleadership.com',
        coachTimezone: 'America/Chicago',
        state: 'complete',
        waitingOn: 'Nobody — complete',
        nextAction: 'None',
        nextActionAt: at(1, 9),
        scheduledStart: at(-4, 15),
        scheduledEnd: at(-4, 15, 45),
        availability: [],
      },
      {
        id: 't-133-3',
        coachName: 'Grace Nakamura',
        coachEmail: 'grace@nakamuraexec.com',
        coachTimezone: 'America/Los_Angeles',
        state: 'complete',
        waitingOn: 'Nobody — complete',
        nextAction: 'None',
        nextActionAt: at(1, 9),
        scheduledStart: at(-2, 13),
        scheduledEnd: at(-2, 13, 45),
        availability: [],
      },
    ],
    timeline: [
      { at: at(-16, 9, 0), actor: 'pm', kind: 'email_in', title: 'Kickoff received from Christine Lane' },
      { at: at(-6, 11, 0), actor: 'pm', kind: 'note', title: 'Elena Sorokina check confirmed complete' },
      { at: at(-4, 16, 0), actor: 'pm', kind: 'note', title: 'Marcus Hill check confirmed complete' },
      { at: at(-2, 14, 0), actor: 'pm', kind: 'note', title: 'Grace Nakamura check confirmed complete — all tracks done' },
      { at: at(-2, 14, 1), actor: 'system', kind: 'note', title: 'Case moved to Awaiting selection', detail: 'Selection prompt scheduled for one business day after the final check.' },
      { at: at(1, 9, 0), actor: 'agent', kind: 'prompt', title: 'Would prompt Christine: “Last chem check finished — find out who Priya wants”', detail: 'Shadow mode: prompt drafted, cancels automatically if a selection is recorded first.', shadow: true },
    ],
  },

  // ── CC-0135 · reschedule requested, audit preserved ──────────────────────
  {
    id: 'cc-demo-135',
    tag: 'CC-0135',
    clientName: 'Marcus Bell',
    clientEmail: 'mbell@summitfg.com',
    clientTimezone: 'America/Denver',
    assistantName: null,
    assistantEmail: null,
    pmName: 'Danielle Neydorff',
    pmEmail: 'dneydorff@arielgroup.com',
    state: 'collecting',
    targetDate: day(10),
    createdAt: at(-5, 14, 10),
    waitingOn: 'Client (Marcus Bell)',
    nextAction: 'Collect fresh windows from Marcus for the rebooked check',
    nextActionAt: at(1, 10),
    escalationReason: null,
    tracks: [
      {
        id: 't-135-1',
        coachName: 'Sofia Reyes',
        coachEmail: 'sofia@reyescoaching.com',
        coachTimezone: 'America/Denver',
        state: 'scheduled',
        waitingOn: 'Nobody — meeting confirmed',
        nextAction: 'Confirm completion after the meeting passes',
        nextActionAt: at(4, 17),
        scheduledStart: at(4, 11),
        scheduledEnd: at(4, 11, 45),
        availability: [],
      },
      {
        id: 't-135-2',
        coachName: 'Ben Carver',
        coachEmail: 'ben@carveradvisory.com',
        coachTimezone: 'America/New_York',
        state: 'waiting_client',
        waitingOn: 'Client (Marcus Bell)',
        nextAction: 'Collect fresh windows from Marcus',
        nextActionAt: at(1, 10),
        scheduledStart: null,
        scheduledEnd: null,
        availability: [
          { party: 'client', sourceText: 'Any day this week 10–12 Mountain', startIso: at(-3, 10), endIso: at(-3, 12), timezone: 'America/Denver', status: 'expired' },
        ],
      },
    ],
    timeline: [
      { at: at(-5, 14, 10), actor: 'pm', kind: 'email_in', title: 'Kickoff received from Danielle Neydorff', detail: 'Two-coach shortlist for Marcus Bell.' },
      { at: at(-3, 10, 0), actor: 'agent', kind: 'event_created', title: 'Invite created — Marcus Bell × Ben Carver', detail: 'Originally booked for this week, 10:30 Mountain.' },
      { at: at(-3, 11, 0), actor: 'agent', kind: 'event_created', title: 'Invite created — Marcus Bell × Sofia Reyes' },
      { at: at(-1, 16, 45), actor: 'coach', kind: 'email_in', title: 'Ben Carver asked to move the meeting', detail: '“A client emergency landed on that morning — can we find another slot next week?”' },
      { at: at(-1, 16, 47), actor: 'agent', kind: 'event_cancelled', title: 'Original invite cancelled — audit retained', detail: 'Original event and its history stay on the case record; only this track reopened.' },
      { at: at(-1, 16, 50), actor: 'agent', kind: 'email_out', title: 'Asked Marcus for fresh windows (this track only)', detail: 'Sofia Reyes’s confirmed meeting is untouched.' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Canned Simulate results — used in mock mode and as the offline fallback if
// the live parse endpoint is unreachable during the demo.
// ---------------------------------------------------------------------------

export const CANNED_KICKOFF_TEXT = `Please coordinate chemistry checks for Jamie Chen with Alex Rivera (alex@riveracoaching.com), Priya Shah (priya@shahleadership.com), and Morgan Lee (morgan@leeadvisory.com). Jamie is in Eastern Time and her assistant Taylor (taylor@meridiancap.com) should be copied. Please complete the checks by August 1. I am the owning PM. — Christine`;

export const CANNED_KICKOFF_PARSE: ChemistryKickoffParse = {
  summary:
    'Three-coach chemistry check for Jamie Chen (Eastern), assistant Taylor copied, owned by Christine, target August 1. All required fields present — safe to open the case and start availability collection.',
  clientName: 'Jamie Chen',
  clientEmail: null,
  clientTimezone: 'America/New_York',
  assistantName: 'Taylor',
  assistantEmail: 'taylor@meridiancap.com',
  pmName: 'Christine',
  targetDate: '2026-08-01',
  coaches: [
    { name: 'Alex Rivera', email: 'alex@riveracoaching.com' },
    { name: 'Priya Shah', email: 'priya@shahleadership.com' },
    { name: 'Morgan Lee', email: 'morgan@leeadvisory.com' },
  ],
  missingFields: ['clientEmail'],
  needsHuman: true,
  confidence: 0.85,
};

export const CANNED_AVAILABILITY_TEXT = `Hi — thanks for the options. Tuesday between 10 and noon Eastern works, and I could also do Thursday after lunch. Friday is out. — Jamie`;

export const CANNED_AVAILABILITY_PARSE: ChemistryAvailabilityParse = {
  summary:
    'Two windows offered: Tuesday 10:00–12:00 Eastern (precise, bookable) and “Thursday after lunch” (not precise enough to book — clarification drafted). Friday explicitly excluded.',
  windows: [
    {
      sourceText: 'Tuesday between 10 and noon Eastern',
      startIso: at(5, 10),
      endIso: at(5, 12),
      timezone: 'America/New_York',
      precise: true,
      note: null,
    },
    {
      sourceText: 'Thursday after lunch',
      startIso: null,
      endIso: null,
      timezone: 'America/New_York',
      precise: false,
      note: 'Not bookable as stated — “after lunch” has no defined start or end.',
    },
  ],
  needsClarification: true,
  clarificationQuestion:
    'For Thursday, what time works best after lunch — for example 1:00, 2:00, or 3:00 Eastern, and until when?',
  proposedMatch: {
    startIso: at(5, 10),
    endIso: at(5, 10, 45),
    timezone: 'America/New_York',
    rationale:
      'Tuesday 10:00–10:45 ET is the earliest precise overlap with the coach’s stated availability and has no conflict on the shared calendar.',
  },
  draftEmails: [
    {
      to: 'jchen@meridiancap.com',
      subject: 'Ariel chemistry check — confirming Tuesday [CC-0142]',
      body: 'Hi Jamie,\n\nThank you! I’ll set up the chemistry check for Tuesday at 10:00 AM Eastern (45 minutes, Teams). A calendar invitation will follow shortly.\n\nCould you also clarify Thursday — what time after lunch works, and until when? That helps me line up your remaining conversations.\n\nBest,\nAriel Scheduling',
    },
  ],
  needsHuman: false,
  confidence: 0.9,
};

/** Prefill for the "Start live case" modal — complete on purpose so the case
 * opens clean; delete the client email live to show the needs-human guard. */
export const LIVE_KICKOFF_PREFILL = `Please coordinate chemistry checks for Dana Whitmore (dana.whitmore@keystonepartners.com) with Alex Rivera (alex@riveracoaching.com) and Priya Shah (priya@shahleadership.com). Dana is in Eastern Time. Please complete the checks by ${new Date(Date.now() + 14 * 86400_000).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}. I am the owning PM. — Christine`;

/** Prefill for advancing a live case as a coach. */
export const COACH_REPLY_PREFILL = `Happy to meet Dana. Tuesday 10 to noon Eastern works great, and Wednesday before 11 is also fine. — Alex`;
